import type { User } from '@/types/auth'
import { createRequestId } from '@/utils/requestId'

export const AUTH_STORAGE_PREFIX = import.meta.env.BASE_URL.startsWith('/admin/') ? 'btp.admin.auth' : 'btp.user.auth'
export const AUTH_TOKEN_KEY = `${AUTH_STORAGE_PREFIX}.token`
export const AUTH_SESSION_KEY = `${AUTH_STORAGE_PREFIX}.session`

export interface AuthSessionSnapshot { token: string | null; revision: string }
export interface StoredAuthSession extends AuthSessionSnapshot {
  version: 1
  user: User | null
  expiresAt: string | null
}

export class AuthStorageError extends Error {
  readonly code = 'AUTH_STORAGE_UNAVAILABLE'
  constructor() { super('無法儲存登入狀態，請允許瀏覽器儲存資料後重新登入。') }
}

function validUser(value: unknown): value is User {
  if (!value || typeof value !== 'object') return false
  const user = value as Partial<User>
  return typeof user.id === 'string' && !!user.id && typeof user.username === 'string'
    && (user.role === 'ADMIN' || user.role === 'USER')
}

function parseSession(raw: string | null): StoredAuthSession | null {
  if (!raw) return null
  try {
    const session = JSON.parse(raw) as StoredAuthSession
    if (session.version !== 1 || typeof session.revision !== 'string' || !session.revision
      || !(session.token === null || (typeof session.token === 'string' && !!session.token))
      || !(session.user === null || validUser(session.user))
      || !(session.expiresAt === null || typeof session.expiresAt === 'string')
      || (!session.token && session.user)) return null
    return session
  } catch { return null }
}

export function guestSession(): StoredAuthSession {
  return { version: 1, token: null, user: null, expiresAt: null, revision: createRequestId() }
}

export function readAuthSession(): StoredAuthSession | null {
  try { return parseSession(localStorage.getItem(AUTH_SESSION_KEY)) }
  catch { throw new AuthStorageError() }
}

export function sameSession(a: AuthSessionSnapshot | null, b: AuthSessionSnapshot | null): boolean {
  return !!a && !!b && a.token === b.token && a.revision === b.revision
}

/** A single storage write keeps the credential, identity and generation together. */
export function writeAuthSession(session: StoredAuthSession): void {
  try { localStorage.setItem(AUTH_SESSION_KEY, JSON.stringify(session)) }
  catch { throw new AuthStorageError() }
}

export function loadAuthSession(): StoredAuthSession {
  try {
    const raw = localStorage.getItem(AUTH_SESSION_KEY)
    const current = parseSession(raw)
    if (current) return current
    const session = guestSession()
    // Migrate once; an invalid new-format record must never resurrect a legacy login.
    if (raw === null) {
      const token = localStorage.getItem(AUTH_TOKEN_KEY)
      if (token) {
        session.token = token
        session.expiresAt = localStorage.getItem(`${AUTH_STORAGE_PREFIX}.expiresAt`)
        try {
          const user: unknown = JSON.parse(localStorage.getItem(`${AUTH_STORAGE_PREFIX}.user`) || 'null')
          session.user = validUser(user) ? user : null
        } catch { session.user = null }
      }
    }
    writeAuthSession(session)
    for (const suffix of ['token', 'user', 'expiresAt']) localStorage.removeItem(`${AUTH_STORAGE_PREFIX}.${suffix}`)
    return session
  } catch { throw new AuthStorageError() }
}
